/**
 * Helpers for extracting selected column data and filtering table rows
 * with robust null / undefined checks.
 */

export interface SelectedColumn {
    id?: number;
    value?: number;
    label?: string;
    disabled?: boolean;
    data?: any;
}

/**
 * Extracts the `data` object(s) from `SelectedColumns` safely.
 * - If `column` is omitted, returns an array of all `data` values (empty array if none).
 * - If `column` is a string, it matches against `label`.
 * - If `column` is a number, it matches against `id` or `value`.
 * - If `column` is an array of strings/numbers, returns an array of matching
 *   `data` objects in the same order (missing entries are `null`).
 * Returns `null` when a single column is requested but not found.
 */
export function extractSelectedColumnData<T extends { SelectedColumns?: SelectedColumn[] }>(
    obj: T | null | undefined,
    column?: string | number | Array<string | number>
): any | any[] | null {
    if (!obj || !Array.isArray(obj.SelectedColumns)) {
        return column === undefined ? [] : null;
    }

    const selected = obj.SelectedColumns;

    if (column === undefined) {
        return selected.map((c) => (c && c.data !== undefined ? c.data : null));
    }

    // If caller passed an array of keys, return an array of data objects
    if (Array.isArray(column)) {
        return column.map((col) => {
            const found = selected.find((c) => {
                if (!c) return false;
                if (typeof col === 'string') return c.label === col;
                return c.id === col || c.value === col;
            });
            return found && found.data !== undefined ? found.data : null;
        });
    }

    // Single value (string | number)
    const found = selected.find((c) => {
        if (!c) return false;
        if (typeof column === 'string') return c.label === column;
        return c.id === column || c.value === column;
    });

    return found && found.data !== undefined ? found.data : null;
}

/**
 * Returns `TableData` rows that only include the keys defined by `SelectedColumns[].label`.
 * - Missing keys in a row are returned as `null` to keep column alignment predictable.
 * - Returns `[]` if input is missing or malformed.
 */
export const filterTableDataBySelected = <T extends { SelectedColumns?: SelectedColumn[]; TableData?: Array<Record<string, any>> }>(
    obj: T | null | undefined
): Array<Record<string, any>> => {
    if (!obj || !Array.isArray(obj.SelectedColumns) || !Array.isArray(obj.TableData)) return [];

    const keys = obj.SelectedColumns.map(c => c && c.label).filter(Boolean) as string[];

    return obj.TableData.map(row => {
        const out: Record<string, any> = {};
        keys.forEach(k => {
            if (row && Object.prototype.hasOwnProperty.call(row, k)) out[k] = row[k];
            else out[k] = null;
        });
        return out;
    });
}

const mergeObjectsByReference = (arr, matchKeys = []) => {
    // Separate objects into two groups: ones with reference properties and ones without
    const refObjects = [];
    const otherObjects = [];

    // First, ensure all objects have empty strings for missing properties
    const allKeys = new Set();
    arr.forEach(obj => {
        Object.keys(obj).forEach(key => allKeys.add(key));
    });

    // Normalize all objects to have all keys with empty strings for missing ones
    const normalizedArr = arr.map(obj => {
        const normalizedObj = {};
        allKeys.forEach((key: any) => {
            normalizedObj[key] = key in obj ? obj[key] : '';
        });
        return normalizedObj;
    });

    normalizedArr.forEach(obj => {
        // Check if object has all matchKeys (reference object)
        const hasAllKeys = matchKeys.every(key => obj[key] !== '');

        if (hasAllKeys) {
            refObjects.push(obj);
        } else {
            otherObjects.push(obj);
        }
    });

    // Create merged results
    const mergedResults = [];
    // debugger
    // For each reference object, find and merge with matching other objects
    // refObjects.forEach(refObj => {
    //     // Find objects in otherObjects that match on all matchKeys
    //     const matchingObjects = otherObjects.filter(otherObj => {
    //         return matchKeys.every(key =>
    //             refObj[key] && otherObj[key] && refObj[key] === otherObj[key]
    //         );
    //     });

    //     // Merge each matching object with the reference object
    //     matchingObjects.forEach(matchObj => {
    //         const mergedObj = { ...refObj };

    //         // Merge properties, preferring non-empty values
    //         Object.keys(matchObj).forEach(key => {
    //             if (matchObj[key] !== '' || mergedObj[key] === '') {
    //                 mergedObj[key] = matchObj[key];
    //             }
    //         });

    //         mergedResults.push(mergedObj);
    //     });
    // });
    // return mergedResults;
    return normalizedArr;
}

export const smartMergeObjects = (arr) => {
    // Find all unique keys in all objects
    const allKeys = new Set();
    arr.forEach(obj => {
        Object.keys(obj).forEach(key => allKeys.add(key));
    });

    // Normalize all objects first
    const normalizedArr = arr.map(obj => {
        const normalizedObj = {};
        allKeys.forEach((key: any) => {
            normalizedObj[key] = key in obj ? obj[key] : '';
        });
        return normalizedObj;
    });

    // Count how many objects have non-empty values for each key
    const keyFrequency = {};
    allKeys.forEach((key: any) => {
        keyFrequency[key] = normalizedArr.filter(obj => obj[key] !== '').length;
    });

    // Determine match keys (keys that have non-empty values in multiple objects but not all)
    const matchKeys = Array.from(allKeys).filter((key: any) => {
        const freq = keyFrequency[key];
        return freq > 1 && freq < normalizedArr.length;
    });

    // If no match keys found, use keys that appear most frequently (but not all)
    if (matchKeys.length === 0) {
        const sortedKeys = Array.from(allKeys)
            .filter((key: any) => keyFrequency[key] > 0)
            .sort((a: any, b: any) => keyFrequency[b] - keyFrequency[a]);
        matchKeys.push(...(sortedKeys.slice(0, 2)));
    }
    return mergeObjectsByReference(normalizedArr, matchKeys);
}